import { forwardRef, type ButtonHTMLAttributes, type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A row of filter chips or tabs that scrolls sideways inside itself instead
 * of widening the page. The edge padding scrolls with the chips, so the first
 * and last chips line up with the page content.
 */
export const ChipRow = forwardRef<HTMLDivElement, { children: ReactNode; className?: string; 'aria-label'?: string }>(
	({ children, className, 'aria-label': ariaLabel }, ref) => (
		<div
			ref={ref}
			role={ariaLabel ? 'group' : undefined}
			aria-label={ariaLabel}
			className={cn(
				'scrollbar-none flex shrink-0 items-center gap-2 overflow-x-auto overscroll-x-contain px-4 py-2 [scroll-padding-inline:1rem]',
				className,
			)}
		>
			{children}
		</div>
	),
);
ChipRow.displayName = 'ChipRow';

interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	active?: boolean;
	icon?: ElementType;
	/** A count shown after the label. */
	count?: ReactNode;
	/** A one-off action (like Clear) rather than an on/off filter, so it isn't announced as a toggle. */
	action?: boolean;
}

/** A pill button for ChipRow: 32px tall with a 44px hit area. */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
	({ active = false, action = false, icon: Icon, count, className, children, type = 'button', ...props }, ref) => (
		<button
			ref={ref}
			type={type}
			aria-pressed={action ? undefined : active}
			className={cn(
				'relative inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[14px] font-medium transition-colors',
				'before:absolute before:-inset-y-1.5 before:inset-x-0 active:opacity-70 disabled:opacity-40',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
				active
					? 'border-foreground bg-foreground text-background'
					: 'border-border bg-background text-foreground dark:bg-zinc-900',
				className,
			)}
			{...props}
		>
			{Icon && <Icon aria-hidden className="size-4 shrink-0" />}
			{children}
			{count !== undefined && count !== null && (
				<span className={cn('tabular-nums', active ? 'text-background/70' : 'text-muted-foreground')}>{count}</span>
			)}
		</button>
	),
);
Chip.displayName = 'Chip';
